import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { FormikProps } from 'formik';
import React from 'react';

import { Alert, Box, Checkbox, FieldError, Flex, Text } from '~/components/blocks';
import { ExternalLink } from '~/components/blocks/ExternalLink';
import { DAY_TYPES } from '~/constants/organization_business_hour';
import { MEDICAL_INFORMATION_NET_URL } from '~/constants/urls';

import { Fields } from '../types';
import { BusinessHoursField } from './BusinessHoursField';

type Props = {
  formik: FormikProps<Fields>;
  disabled: boolean;
  hasOrganizationBusinessHours: boolean;
};

const toDayColor = (dayKey: string) => {
  if (dayKey === 'sunday' || dayKey === 'holiday') {
    return 'pink';
  } else if (dayKey === 'saturday') {
    return 'blue';
  } else {
    return 'black';
  }
};

const Week = styled(Box)(({ theme }) =>
  css({
    marginTop: theme.space.l,
    '& + &': {
      marginTop: theme.space.l,
    },
  }),
);

const WeekTitle = styled(Flex)(({ theme }) =>
  css({
    justifyContent: 'space-between',
    padding: theme.space.m,
    backgroundColor: theme.colors.background.bg,
  }),
);

export const BusinessHoursDisplay = (props: Props) => {
  const { formik, disabled, hasOrganizationBusinessHours } = props;
  const theme = useTheme();

  return (
    <>
      {formik.submitCount > 0 && formik.errors.businessHours && (
        <Box mb={theme.space.m}>
          <FieldError error={formik.errors.businessHours as string} />
        </Box>
      )}
      {!hasOrganizationBusinessHours && (
        <Alert status="warning" mb={theme.space.m}>
          <Text size="s">
            <ExternalLink url={MEDICAL_INFORMATION_NET_URL} fontSize="s">
              医療情報ネット
            </ExternalLink>
            から営業時間情報を取得できた薬局は、その情報をもとに自動設定しています。内容をご確認のうえ、必要に応じて修正してください
          </Text>
        </Alert>
      )}
      <Alert status="info" mb={theme.space.m}>
        <Text size="s">
          営業時間はプルダウンで設定し、例外的な
          <br />
          内容のみ補足欄にご入力ください
          <br />
          （例：「第1・3日曜日は営業」など）
        </Text>
      </Alert>
      {DAY_TYPES.map((day, dayIdx) => {
        const field = formik.values.businessHours[dayIdx];

        return (
          <Week key={day.key}>
            <WeekTitle>
              <Text fontWeight="bold" color={toDayColor(day.key)}>{`${day.label}`}</Text>
              <Checkbox
                disabled={disabled}
                label="休業日"
                checked={!field.enabled}
                onChange={() =>
                  formik.setFieldValue(`businessHours[${dayIdx}].enabled`, !field.enabled)
                }
              />
            </WeekTitle>
            {field.enabled && (
              <Box marginTop={theme.space.m} paddingBottom={theme.space.m}>
                <BusinessHoursField
                  formik={formik}
                  disabled={disabled}
                  dayIdx={dayIdx}
                  timeRanges={field.timeRanges}
                />
              </Box>
            )}
          </Week>
        );
      })}
    </>
  );
};
